import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { OpenedView, OpenedViewsContext } from "src/routing/keepAlive/OpenedViewsContext.ts";

interface OpenedViewsContextProviderProps {
	children: React.ReactNode;
}

/** Keeps recently opened views mounted, so that their state survives switching between views. */
export function OpenedViewsContextProvider({ children }: OpenedViewsContextProviderProps) {
	const [openedViews, setOpenedViews] = React.useState<OpenedView[]>([]);
	const addOpenedView = React.useCallback((viewKey: string) => {
		setOpenedViews((current) => withOpenedView(current, viewKey));
	}, []);
	const value = React.useMemo(() => ({ openedViews, addOpenedView }), [openedViews, addOpenedView]);
	return <OpenedViewsContext.Provider value={value}>{children}</OpenedViewsContext.Provider>;
}

const OPENED_VIEWS_TO_KEEP_COUNT = 8;

/**
 * Keeps defined count of views in the state. If the count exceeds, the oldest view is replaced.
 * Position of views in the state are preserved on their original position to prevent
 * re-rendering of the views.
 */
function withOpenedView(openedViews: OpenedView[], viewKey: string): OpenedView[] {
	const currentIndex = openedViews.findIndex((view) => view.viewKey === viewKey);
	if (currentIndex != -1) {
		return replaceViewAtIndex(currentIndex);
	}

	const shouldRemoveSomeView = openedViews.length >= OPENED_VIEWS_TO_KEEP_COUNT;
	if (shouldRemoveSomeView) {
		return replaceViewAtIndex(findOldestViewIndex());
	} else {
		return replaceViewAtIndex(openedViews.length);
	}

	function replaceViewAtIndex(index: number) {
		const newOpenedViews = openedViews.slice();
		newOpenedViews[index] = {
			creationTime: dayjs(),
			viewKey: viewKey,
		};
		return newOpenedViews;
	}

	function findOldestViewIndex(): number {
		interface ReduceAccumulator {
			earliestCreationTime: Dayjs | null;
			index: number;
		}

		return openedViews.reduce<ReduceAccumulator>(
			(acc, view, index) => {
				if (acc.earliestCreationTime == null || view.creationTime.isBefore(acc.earliestCreationTime)) {
					return {
						earliestCreationTime: view.creationTime,
						index: index,
					};
				}
				return acc;
			},
			{ earliestCreationTime: null, index: 0 },
		).index;
	}
}
